/**
 * 用户报表知识库加载：从项目内已有报表 XML 中提取字段绑定知识。
 *
 * 定位：用户已有报表中的字段绑定是最准确的真值数据。通过解析报表 XML，
 * 提取 (label, field, zone, side) 四元组，生成高置信度的知识库。
 *
 * 用法：
 *   const dict = await loadUserReportBindings('./reports');
 *   // dict 包含从报表中学到的所有绑定模式
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import {
  aggregateBindings,
  emptyBindingDict,
  mineFromIr,
  type BindingDict,
  type BindingKnowledge,
} from '../core/template/mineFromIr.js';
import { parseReport } from '../core/xml/parse.js';
import { getLogger } from '../core/logger.js';

export type { BindingDict } from '../core/template/mineFromIr.js';

const log = getLogger('scripts:load-user-reports');

/**
 * 递归扫描目录，找出所有 *.xml 文件
 */
function scanReportFiles(dir: string): string[] {
  const files: string[] = [];

  function walk(current: string) {
    try {
      const entries = fs.readdirSync(current, { withFileTypes: true });
      for (const entry of entries) {
        const fullPath = path.join(current, entry.name);
        if (entry.isDirectory()) {
          walk(fullPath);
        } else if (entry.isFile() && entry.name.endsWith('.xml')) {
          files.push(fullPath);
        }
      }
    } catch (error) {
      log.warn(`⚠️ 扫描目录失败 (${current}): ${(error as Error).message}`);
    }
  }

  walk(dir);
  return files;
}

/**
 * 从报表 XML 中解析字段绑定。
 *
 * 真实 BaskReport XML 的数据位是嵌套的 <dataset-content dataset="..." field="..."
 * aggregate-type="..."/>，标签位是 <text-content><![CDATA[...]]></text-content>，
 * 不存在扁平的 <content kind="dataset"> 结构——因此不能靠正则猜测 XML 形状，
 * 统一走 parseReport() → IR → mineFromIr() 提取（见 src/core/template/mineFromIr.ts）。
 */
function extractBindingsFromReportXml(
  filePath: string,
  xmlContent: string,
  maxRows: number = 100,
): BindingKnowledge[] {
  try {
    const ir = parseReport(xmlContent);
    if (!ir) return [];
    return mineFromIr(ir, path.basename(filePath), maxRows);
  } catch (error) {
    log.warn(`⚠️ 解析报表 XML 失败 (${path.basename(filePath)}): ${(error as Error).message}`);
  }
  return [];
}

/**
 * 主函数：从用户报表目录加载绑定知识库
 */
export async function loadUserReportBindings(
  reportsDir: string,
  maxRows: number = 100,
): Promise<BindingDict> {
  log.info(`🔍 开始扫描用户报表目录：${reportsDir}`);

  if (!fs.existsSync(reportsDir)) {
    log.warn(`⚠️ 目录不存在：${reportsDir}`);
    return emptyBindingDict();
  }

  const reportFiles = scanReportFiles(reportsDir);
  log.info(`📄 找到 ${reportFiles.length} 个报表文件`);

  const allBindings: BindingKnowledge[] = [];

  for (const file of reportFiles) {
    try {
      const xmlContent = fs.readFileSync(file, 'utf-8');
      const bindings = extractBindingsFromReportXml(file, xmlContent, maxRows);
      allBindings.push(...bindings);
      log.info(`✓ ${path.basename(file)}: ${bindings.length} 个绑定`);
    } catch (error) {
      log.warn(`✗ ${path.basename(file)}: ${(error as Error).message}`);
    }
  }

  log.info(`📊 总计提取 ${allBindings.length} 个绑定`);

  const bindingDict = aggregateBindings(allBindings);
  log.info(`✓ 用户报表知识库已生成`);
  log.info(`  - ${Object.keys(bindingDict.labels).length} 个标签`);
  log.info(`  - ${Object.keys(bindingDict.positions).length} 个位置`);
  log.info(`  - ${Object.keys(bindingDict.patterns).length} 个模式`);

  return bindingDict;
}
